import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@patternfly/react-core';
import { DateTime, Duration } from 'luxon';
import { FeatureFlag } from '@ole-ui/ole-ui-components';
import type { UserLab } from 'types/labs';
import FormattedDateTime from 'components/FormattedDateTime';
import LabEnvironmentStore from '../../store';

type AutostopTimerProps = {
  labStore: LabEnvironmentStore;
};

const AutostopTimer: React.FC<AutostopTimerProps> = ({ labStore }) => {
  const { t } = useTranslation();
  const userLab = labStore.userLab as UserLab;

  const renderAutoStop = () => {
    /* eslint-disable camelcase */
    const time = userLab?.scheduled_shutdown_time;
    const subModality = userLab?.sub_modality;
    if (!time) {
      return null;
    }

    const now = DateTime.utc();
    const then = DateTime.fromISO(time, { zone: 'utc' });
    const hoursUntil =
      Duration.fromMillis(then.toMillis() - now.toMillis()).as('hours') + 1;

    return (
      <div className="autostop-timers-container">
        <span>
          {t('Auto-stop')} <FormattedDateTime fromNow>{time}</FormattedDateTime>
          .
        </span>

        {subModality !== 'ls000' && hoursUntil < 12 && (
          <Button
            data-analytics-id="auto-stop-vt-dash-lp"
            onClick={() => labStore.doCommand('app_autostop_extend', null)}
            size="sm"
            title={t('Add One Hour')}
            variant="secondary"
          >
            {t('+ Add 1 hour')}
          </Button>
        )}
      </div>
    );
  };

  const renderAutoDestroy = () => {
    const { labDeleteThreshold } = labStore;
    const time = userLab?.scheduled_destroy_time;
    const subModality = userLab?.sub_modality;
    if (!time) {
      return null;
    }

    const now = DateTime.utc();
    const then = DateTime.fromISO(time, { zone: 'utc' });
    const daysUntil =
      Duration.fromMillis(then.toMillis() - now.toMillis()).as('days') + 1;

    return (
      <div className="autostop-timers-container">
        <span>
          {t('Auto-destroy')}{' '}
          <FormattedDateTime fromNow>{time}</FormattedDateTime>.
        </span>

        {subModality !== 'ls000' && daysUntil < labDeleteThreshold && (
          <Button
            data-analytics-id="auto-destroy-vt-dash-lp"
            onClick={() => labStore.doCommand('app_autodelete_extend', null)}
            size="sm"
            title={t('Add One Day')}
            variant="secondary"
          >
            {t('+ Add 1 day')}
          </Button>
        )}
      </div>
    );
  };

  const autoStop = renderAutoStop();
  const autoDestroy = renderAutoDestroy();

  if (!autoStop && !autoDestroy) {
    return null;
  }

  return (
    <div className="autostop-timers">
      <FeatureFlag defaultFlagValue flagName="auto_stop" section="lab">
        {autoStop}
      </FeatureFlag>

      <FeatureFlag defaultFlagValue flagName="auto_destroy" section="lab">
        {autoDestroy}
      </FeatureFlag>
    </div>
  );
};

export default observer(AutostopTimer);
